import { ReviewData } from '@aidkitorg/aidkit/lib/distro/review/distroReview';
import {
  Button,
  Heading,
  Tooltip
} from '@aidkitorg/component-library';
import { useMemo } from 'react';

interface DistroReviewSummaryProps {
  reviewData: ReviewData | null;
  reviewId: string;
  isClosing: boolean;
  isPublishing: boolean;
  onCloseReviewClick: () => void;
  onUpdate?: () => void;
  surveyHashMismatch?: boolean;
}

const getWorkflowTooltip = (workflowName: string): string => {
  switch (workflowName) {
    case 'data_snapshot':
      return 'Creates a snapshot of current applicant data. Used by other workflows.';
    case 'data_snapshot_simulated':
      return 'Creates another snapshot for the roboscreener simulation to act upon. Used by other workflows.';
    case 'roboscreener_simulation':
      return 'Runs a roboscreener simulation against the snapshot. Used by other workflows.';
    case 'notifications':
      return 'Analyzes notification changes and calculates their impact on applicants.';
    case 'changes':
      return 'Compares the old and new survey versions to identify all differences between them.';
    case 'target_fields':
      return 'Analyzes how survey definition changes affect applicants\' target field values.';
    case 'data_snapshot_cleanup':
      return 'Cleans up temporary data snapshots and related tables after all other workflows have completed.';
    default:
      return 'Unknown workflow.';
  }
};

export default function DistroReviewSummary({ 
  reviewData, 
  reviewId, 
  isClosing, 
  isPublishing, 
  onCloseReviewClick,
  onUpdate,
  surveyHashMismatch
}: DistroReviewSummaryProps) {
  const isOpen = reviewData?.status === 'review_pending' || reviewData?.status === 'approved';

  const workflows = useMemo(() =>
    reviewData?.workflowStatuses?.map((workflow) => ({
      ...workflow,
      durationInSeconds: workflow.completedAt && workflow.startedAt
        ? ((new Date(workflow.completedAt).getTime() - new Date(workflow.startedAt).getTime()) / 1000).toFixed(1)
        : null,
    })) || [],
  [reviewData]);

  return (
    <>
      <div className="flex flex-row justify-between items-start">
        <Heading level="h2" className="text-lg font-semibold">Summary</Heading>
        {isOpen && <Button
          onClick={onCloseReviewClick}
          isDisabled={isClosing || isPublishing}
          loading={isClosing}
          variant="danger"
        >
          Close Review
        </Button>
        }
      </div>
      <div className="text-sm text-gray-500">
        Created by {reviewData?.createdBy} on {reviewData?.createdAt && new Date(reviewData.createdAt).toLocaleString()}
      </div>

      {reviewData?.workflowStatuses && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2 max-w-4xl">
          <Heading level="h3" className="text-base font-semibold text-gray-900 mb-4">Workflow Statuses</Heading>
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-sm font-medium text-gray-900 py-2">Workflow</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2 px-3">Status</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((workflow) => (
                <tr key={workflow.workflowName} className="border-b last:border-b-0">
                  <td className="py-2 text-sm font-medium text-gray-900">
                    <Tooltip text={getWorkflowTooltip(workflow.workflowName)} side="right">
                      <span className="cursor-help border-b border-dotted border-gray-400">
                        {workflow.workflowName}
                      </span>
                    </Tooltip>
                  </td>
                  <td className="py-2 px-3">
                    {workflow.status === 'failed' ? (
                      <span className="text-sm text-red-500 whitespace-pre-wrap">
                        {workflow.status}{workflow.error ? `: ${workflow.error}` : ''}
                      </span>
                    ) : workflow.status === 'completed' && workflow.warning ? (
                      <span className="text-sm text-orange-600 whitespace-pre-wrap">{workflow.status} with warning: {workflow.warning}</span>
                    ) : workflow.status === 'completed' ? (
                      <span className="text-sm text-green-600 whitespace-pre-wrap">{workflow.status}</span>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap">{workflow.status}</span>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-700 whitespace-pre-wrap">
                    {workflow.durationInSeconds ? `${workflow.durationInSeconds} s` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
} 
